import { Maybe } from '@monetize/types/common';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { IAccountRespSchema } from './account.types';
import { IOfferingRes } from './offering.types';
import { IProduct } from './product.types';
import {
  IQuoteBillingScheduleRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
} from './quote.types';
import { IRateResSchema } from './rate.types';

export const QuoteTemplateConfigSchema = z.object({
  showBillingSchedule: z.boolean(),
  showYearlyBreakDown: z.boolean().optional(),
  showCredits: z.boolean(),
  showBillingFrequency: z.boolean(),
  showDiscount: z.boolean(),
  showProration: z.boolean(),
  showPricePerUnit: z.boolean(),
  showProductDescription: z.boolean(),
  showPricing: z.boolean(),
  showPriorContractOnAmendments: z.boolean().optional(),
  showDeltaViewOnAmendments: z.boolean().optional(),
  showRateDescription: z.boolean().optional(),
  showOfferingDescription: z.boolean().optional(), // need to remove when BE will be fixed
  showSigningPage: z.boolean().optional(),
  showUnusedItems: z.boolean().optional(),
  showRateName: z.boolean().default(true),
  showLineLevelAmount: z.boolean().default(true),
  showHeaderTotal: z.boolean().default(true),
  showDiscountSection: z.boolean().default(true),
  defaultTemplate: z.string().optional(),
  hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments: z
    .boolean()
    .optional(),
});

export type IQuoteTemplateConfigSchema = z.infer<
  typeof QuoteTemplateConfigSchema
>;

export interface IQuoteTemplateConfigRes {
  showBillingSchedule: boolean;
  showYearlyBreakDown?: boolean;
  showBillingFrequency: boolean;
  showCredits: boolean;
  showDiscount: boolean;
  showProration: boolean;
  showPricePerUnit: boolean;
  showProductDescription: boolean;
  showPricing: boolean;
  showPriorContractOnAmendments?: boolean;
  showDeltaViewOnAmendments?: boolean;
  showRateDescription?: boolean;
  showOfferingDescription?: boolean; // need to remove when BE will be fixed
  showSigningPage?: boolean;
  showUnusedItems?: boolean;
  hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments?: boolean;
  showRateName: boolean;
  showLineLevelAmount: boolean;
  showHeaderTotal: boolean;
  showDiscountSection: boolean;
  defaultTemplate?: string;
}

export interface IQuoteTemplateFormProps {
  source: 'tenant' | 'quote';
  formState: UseFormReturn<IQuoteTemplateConfigRes>;
  isDisabled?: boolean;
  requireSigningPage?: boolean;
  isAmendmentV2Quote?: boolean;
}

export interface QuoteTemplateSigner {
  name: string;
  email: string;
  jobTitle: null;
  signatureTag: string;
  dateSignedTag: string;
}

export interface QuotePdfTemplate {
  account: IAccountRespSchema;
  billingSchedule?: IQuoteBillingScheduleRespSchema;
  billingScheduleDiff?: Maybe<
    Omit<IQuoteBillingScheduleRespSchema, 'incrementalPeriod'> & {
      incrementalPeriod: Required<IQuoteBillingScheduleRespSchema>['incrementalPeriod'];
    }
  >;
  companyLogo: string | null;
  esigners: {
    internal: QuoteTemplateSigner[];
    external: QuoteTemplateSigner[];
    additionalSigners: QuoteTemplateSigner[];
  };
  hidePriorContract?: boolean;
  offerings: Record<string, IOfferingRes>;
  owner: string;
  priorContract?: IQuoteRespSchema;
  products: Record<string, IProduct>;
  quote: IQuoteRespSchema;
  quoteDiscounts: { discounts: any[]; total: number };
  quoteDisplayConfiguration: IQuoteTemplateConfigRes;
  quoteOfferingPrices?: IQuotePrice[];
  rates: Record<string, IRateResSchema>;
}
